/**
 * Recraft family on fal.ai (Phase 1, U8): recraft-v3, recraft-v4, recraft-v4-pro. Hidden in the
 * node menu (CLAUDE.md), still served to saved flows.
 *
 * Replaces: falai.ts async image arm `case 'recraft-v3' / 'recraft-v4' / 'recraft-v4-pro'`, the
 * generic builder in ProviderService.generateImageFalAI, and the FAL_IMAGE_MODELS rows (Recraft
 * has NO image-to-image endpoint). Pricing: CreditService's generic tail (base x legacy ladder).
 *
 * PURE: `import type` + sibling modules only (purity.test).
 *
 * Legacy quirks reproduced on purpose:
 *   - editor always sends `style` (RECRAFT_STYLES[style] || 'realistic_image'); headless never
 *     does. V4 / V4 Pro schemas have no `style` field at all (dead key there).
 *   - num_images: 1 is absent from every Recraft schema (dead key, both surfaces).
 *   - editor input images are dropped (t2i endpoint, no image_urls); headless posts them as
 *     image_urls to the text-to-image endpoint, whose schema has no such field (dead key).
 */
import type { ModelDescriptor, Sku } from '../types';
import { FAL_IMAGE_SIZE_PRESETS } from '../_shared';

/** falai.ts RECRAFT_STYLES. */
const RECRAFT_STYLES = { realistic: 'realistic_image', digital: 'digital_illustration', vector: 'vector_illustration' } as const;

const recraft = (o: { id: 'recraft-v3' | 'recraft-v4' | 'recraft-v4-pro'; name: string; endpoint: string; sku: Sku }): ModelDescriptor => {
  const hasStyle = o.id === 'recraft-v3';
  return {
    id: o.id, provider: 'falai', face: 'image', displayName: o.name, ledgerName: o.name, status: 'hidden',
    prompt: { field: 'prompt', required: false },
    modes: {
      // No edit endpoint. Only headless ever carries refs here (see header).
      t2i: { endpoint: o.endpoint, ports: [], media: { refs: { field: 'image_urls', many: true, encode: 'image' } } },
    },
    fixed: { enable_safety_checker: false },
    params: [
      { id: 'aspectRatio', type: 'string', default: '1:1', invalid: 'passthrough',
        wire: { kind: 'imageSize', field: 'image_size', presets: FAL_IMAGE_SIZE_PRESETS, fallback: 'square_hd' },
        ui: { label: 'Aspect ratio', tier: 'basic' } },
      // Any value outside RECRAFT_STYLES falls back to 'realistic_image' (coerce to the default).
      { id: 'style', type: 'enum', values: ['realistic', 'digital', 'vector'], default: 'realistic',
        invalid: { coerce: { kind: 'default' } }, wire: false,
        ui: { label: 'Style', tier: 'basic', optionLabels: { realistic: 'Realistic', digital: 'Digital illustration', vector: 'Vector illustration' } } },
      { id: 'negativePrompt', type: 'string', default: null, invalid: 'passthrough', wire: false },
      { id: 'resolution', type: 'string', default: null, role: 'tier', invalid: 'passthrough', wire: false },
    ],
    legacy: {
      wire: [
        { field: 'style', fromParam: 'style', map: RECRAFT_STYLES, surface: 'editor',
          why: hasStyle
            ? 'editor-only (headless omits it and fal defaults to realistic_image)'
            : 'editor-only; absent from the Recraft V4 schemas (oracle 2026-09-19): dead key',
          retire: hasStyle ? 'owner-approval' : 'inert-cleanup' },
        { field: 'num_images', value: 1, why: 'absent from every Recraft schema (oracle 2026-09-19)', retire: 'inert-cleanup' },
        { field: 'output_format', value: 'png', surface: 'headless',
          why: 'headless generic builder; absent from the Recraft schemas', retire: 'inert-cleanup' },
        { field: 'negative_prompt', fromParam: 'negativePrompt', surface: 'headless',
          why: 'headless generic builder when truthy; absent from the Recraft schemas', retire: 'inert-cleanup' },
        // Declaration only (no value, so assemble() adds nothing): the key itself is emitted by the
        // t2i `refs` media wire. Listing it here books it as legacy debt; LegacyWire has no media form.
        { field: 'image_urls', modes: ['t2i'], surface: 'headless',
          why: 'headless posts input images to the text-to-image endpoint, whose schema has no image_urls (dead key; emitted by the refs media wire)',
          retire: 'inert-cleanup' },
      ],
      notes: ['Headless posts input images as image_urls to the text-to-image endpoint (not in its schema): the media wire reproduces it.'],
    },
    pricing: { unit: 'generation', units: { fixed: 1 }, rounding: 'generic', policy: 'fal-image-35', skus: [o.sku] },
    schemaVerified: '2026-09-19',
  };
};

export const RECRAFT_V3 = recraft({
  id: 'recraft-v3', name: 'Recraft V3', endpoint: 'fal-ai/recraft/v3/text-to-image',
  sku: {
    id: 'recraft-v3',
    rate: { ladder: { base: 18, by: 'resolution', curve: 'legacy-resolution-v1' } },
    vendor: { usd: 0.04, per: 'image', field: 'pricingInfoOverride', source: 'fal-ai/recraft/v3/text-to-image',
      text: 'Your request will cost **$0.04** per image (or **$0.08** if you are using a vector style). For $1 you can run this model approximately **25** times.',
      verified: '2026-09-19' },
    marginException: '18 = ceil(0.04 x 450) (policy 19); the vector style costs $0.08 at fal and is billed the same 18. Kept (P1 price-neutral); owner decision.',
    registry: { displayName: 'Recraft V3', operationType: 'image', flatRate: false },
  },
});

export const RECRAFT_V4 = recraft({
  id: 'recraft-v4', name: 'Recraft V4', endpoint: 'fal-ai/recraft/v4/text-to-image',
  sku: {
    id: 'recraft-v4',
    rate: { ladder: { base: 19, by: 'resolution', curve: 'legacy-resolution-v1' } },
    vendor: { usd: 0.04, per: 'image', field: 'sidebar', source: 'fal-ai/recraft/v4/text-to-image (rendered "Your request will cost")',
      text: 'Your request will cost $0.04 per image.', verified: '2026-09-19' },
    registry: { displayName: 'Recraft V4', operationType: 'image', flatRate: false },
  },
});

export const RECRAFT_V4_PRO = recraft({
  id: 'recraft-v4-pro', name: 'Recraft V4 Pro', endpoint: 'fal-ai/recraft/v4/pro/text-to-image',
  sku: {
    id: 'recraft-v4-pro',
    rate: { ladder: { base: 116, by: 'resolution', curve: 'legacy-resolution-v1' } },
    vendor: { usd: 0.25, per: 'image', field: 'sidebar', source: 'fal-ai/recraft/v4/pro/text-to-image (rendered "Your request will cost")',
      text: 'Your request will cost $0.25 per image.', verified: '2026-09-19' },
    registry: { displayName: 'Recraft V4 Pro', operationType: 'image', flatRate: false },
  },
});

export const RECRAFT_MODELS: readonly ModelDescriptor[] = [RECRAFT_V3, RECRAFT_V4, RECRAFT_V4_PRO];
